import React, { useState, useEffect } from "react";
import {
  Search,
  AlertCircle,
  Loader2,
  PanelRightClose,
  ShieldCheck,
  ShieldAlert,
  Shirt,
  Calendar,
  X,
  FileText,
  User,
  Clock,
  AlertTriangle,
} from "lucide-react";
import api from "../../connector/api";

const CitizenshipTab = ({
  loading,
  error,
  activeClass,
  activeStudents,
  isRightSidebarOpen,
  setIsRightSidebarOpen,
  addToast,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [citizenshipData, setCitizenshipData] = useState({});
  const [activeTerm, setActiveTerm] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  // Modal State for Dress Code & Behavior Violations
  const [modalType, setModalType] = useState(null); // 'dress' | 'behavior' | null
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentViolations, setStudentViolations] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // Fetch citizenship records per active term
  useEffect(() => {
    const fetchCitizenshipData = async () => {
      if (!activeClass?.id) return;
      setDataLoading(true);

      try {
        let currentTerm = activeClass?.school_year?.active_term || null;

        if (!currentTerm) {
          const termRes = await api.get("/year-terms/");
          const termsData = Array.isArray(termRes.data)
            ? termRes.data
            : termRes.data.results || [];

          const activeJunction = termsData.find((item) => item.is_current);
          currentTerm = activeJunction
            ? activeJunction.term
            : termsData[0]?.term || termsData[0] || null;
        }

        setActiveTerm(currentTerm);

        const params = {
          classroom: activeClass.id,
        };
        if (currentTerm?.id) {
          params.term = currentTerm.id;
        }

        const res = await api.get("/teacher/citizenship/", { params });

        const list = Array.isArray(res.data)
          ? res.data
          : res.data.results || [];

        const recordsMap = {};
        list.forEach((item) => {
          recordsMap[String(item.student)] = item;
        });
        setCitizenshipData(recordsMap);
      } catch (err) {
        console.error("Error loading citizenship data:", err);
        addToast?.("Could not load citizenship record data", "error");
      } finally {
        setDataLoading(false);
      }
    };

    fetchCitizenshipData();
  }, [activeClass?.id, addToast]);

  // Handler: Fetch Dress Code Violations
  const handleOpenDressModal = async (student, count) => {
    if (count === 0) return;

    setSelectedStudent(student);
    setModalType("dress");
    setModalLoading(true);
    setStudentViolations([]);

    try {
      const params = {
        student: student.id,
        all_dates: "true",
      };
      if (activeTerm?.id) {
        params.term = activeTerm.id;
      }

      const res = await api.get("/teacher/uniform-infractions/", { params });
      const records = Array.isArray(res.data)
        ? res.data
        : res.data.results || [];

      setStudentViolations(records);
    } catch (err) {
      console.error("Failed to load student dress code infractions:", err);
      addToast?.("Unable to fetch dress code history", "error");
    } finally {
      setModalLoading(false);
    }
  };

  // Handler: Fetch Behavior Disciplinary Referrals
  const handleOpenBehaviorModal = async (student, count) => {
    if (count === 0) return;

    setSelectedStudent(student);
    setModalType("behavior");
    setModalLoading(true);
    setStudentViolations([]);

    try {
      const params = {
        student: student.id,
      };
      if (activeTerm?.id) {
        params.term = activeTerm.id;
      }

      const res = await api.get("/teacher/discipline-referrals/", { params });
      const records = Array.isArray(res.data)
        ? res.data
        : res.data.results || [];

      setStudentViolations(records);
    } catch (err) {
      console.error("Failed to load student behavior referrals:", err);
      addToast?.("Unable to fetch behavior history", "error");
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedStudent(null);
    setModalType(null);
    setStudentViolations([]);
  };

  const filteredStudents = (activeStudents || []).filter((student) => {
    const query = searchTerm.toLowerCase().trim();
    const fullName = `${student.last_name || ""} ${student.first_name || ""}`.toLowerCase();
    const studentId = String(student.student_id_number || student.id || "").toLowerCase();
    return !query || fullName.includes(query) || studentId.includes(query);
  });

  if (loading || dataLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-12 text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin mb-2" />
        <span className="text-xs font-semibold">Loading citizenship records...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 max-w-md">
          <AlertCircle className="w-5 h-5 mx-auto mb-2 text-rose-600" />
          <p className="text-xs font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto w-full">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-b border-zinc-200/80 pb-5">
        <div className="flex items-center gap-2 flex-1 max-w-xl">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search student name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs font-medium placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all shadow-xs"
            />
          </div>
          <div className="px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-600 flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-zinc-500" />
            <span>Term: {activeTerm?.name || "Active Term"}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 justify-end">
          {!isRightSidebarOpen && (
            <button
              type="button"
              onClick={() => setIsRightSidebarOpen?.(true)}
              className="h-8 px-3 border border-zinc-200 hover:bg-zinc-100 rounded-lg text-zinc-700 transition-colors flex items-center gap-1.5 text-xs font-semibold shrink-0 cursor-pointer shadow-xs"
            >
              <PanelRightClose className="w-4 h-4 text-zinc-600" />
              <span>Summary</span>
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white border border-zinc-200 rounded-lg shadow-xs overflow-hidden">
        <div className="overflow-x-auto custom-scroll">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50/80 border-b border-zinc-200 text-[11px] font-bold text-zinc-600 uppercase tracking-wider">
                <th className="py-3 px-4 w-64 bg-white">Student Name</th>
                <th className="py-3 px-4 w-36 text-center">Dress Code</th>
                <th className="py-3 px-4 w-36 text-center">Behavior</th>
                <th className="py-3 px-4 w-28 text-center">Total Points</th>
                <th className="py-3 px-4 w-36 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 text-xs font-medium text-zinc-700">
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student, idx) => {
                  const studentKey = String(student.id);
                  const record = citizenshipData[studentKey] || {};

                  const lastName = student.last_name || "";
                  const firstName = student.first_name || "";
                  const fullName =
                    lastName && firstName
                      ? `${lastName}, ${firstName}`
                      : student.name || `${firstName} ${lastName}`.trim();

                  // Counts
                  const dressCount =
                    record.dress_code_violations ??
                    student.dress_code_violations ??
                    0;
                  const behaviorCount =
                    record.behavior_violations ??
                    student.behavior_violations ??
                    0;

                  const totalPoints =
                    record.total_points ?? (dressCount + behaviorCount);

                  // Status determination
                  const status =
                    record.status ||
                    (totalPoints === 0
                      ? "Exceeds Standard"
                      : totalPoints >= 5
                      ? "Unsatisfactory"
                      : "Satisfactory");

                  return (
                    <tr
                      key={student.id}
                      className="hover:bg-zinc-50/60 transition-colors"
                    >
                      {/* Student Name */}
                      <td className="py-3 px-4 font-semibold text-zinc-900 align-middle">
                        <div>
                          {idx + 1}) {fullName}
                        </div>
                      </td>

                      {/* Dress Code Count */}
                      <td className="py-3 px-4 text-center align-middle">
                        <button
                          type="button"
                          disabled={dressCount === 0}
                          onClick={() => handleOpenDressModal(student, dressCount)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                            dressCount > 0
                              ? "bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 cursor-pointer shadow-2xs"
                              : "bg-zinc-100 text-zinc-500 border border-zinc-200/80 cursor-default"
                          }`}
                        >
                          <Shirt
                            className={`w-3.5 h-3.5 ${
                              dressCount > 0 ? "text-amber-600" : "text-zinc-400"
                            }`}
                          />
                          <span>{dressCount}</span>
                          <span className="text-[10px] opacity-75 font-normal">
                            {dressCount === 1 ? "time" : "times"}
                          </span>
                        </button>
                      </td>

                      {/* Behavior Referral Count */}
                      <td className="py-3 px-4 text-center align-middle">
                        <button
                          type="button"
                          disabled={behaviorCount === 0}
                          onClick={() => handleOpenBehaviorModal(student, behaviorCount)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                            behaviorCount > 0
                              ? "bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100 cursor-pointer shadow-2xs"
                              : "bg-zinc-100 text-zinc-500 border border-zinc-200/80 cursor-default"
                          }`}
                        >
                          <AlertTriangle
                            className={`w-3.5 h-3.5 ${
                              behaviorCount > 0 ? "text-rose-600" : "text-zinc-400"
                            }`}
                          />
                          <span>{behaviorCount}</span>
                          <span className="text-[10px] opacity-75 font-normal">
                            {behaviorCount === 1 ? "time" : "times"}
                          </span>
                        </button>
                      </td>

                      {/* Total Points */}
                      <td className="py-3 px-4 text-center align-middle font-bold text-zinc-800 text-xs">
                        {totalPoints}
                      </td>

                      {/* Citizenship Status Badge */}
                      <td className="py-3 px-4 text-center align-middle">
                        {status === "Exceeds Standard" ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200/60 px-2.5 py-0.5 rounded-md">
                            <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                            Exceeds Standard
                          </span>
                        ) : status === "Satisfactory" ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2.5 py-0.5 rounded-md">
                            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                            Satisfactory
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200/60 px-2.5 py-0.5 rounded-md">
                            <ShieldAlert className="w-3.5 h-3.5 text-rose-600" />
                            Unsatisfactory
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="py-12 text-center text-xs text-zinc-400 italic"
                  >
                    No matching student records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Violation Detail Modal (Dress Code OR Behavior) */}
      {selectedStudent && modalType && (
        <div className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="relative w-full max-w-xl bg-white border border-zinc-200 rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[85vh] text-zinc-800 text-xs animate-in fade-in zoom-in-95 duration-150">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200 bg-zinc-50/80">
              <div className="flex items-center gap-2.5">
                <div
                  className={`p-2 border rounded-lg ${
                    modalType === "dress"
                      ? "bg-amber-100/80 border-amber-200 text-amber-700"
                      : "bg-rose-100/80 border-rose-200 text-rose-700"
                  }`}
                >
                  {modalType === "dress" ? (
                    <Shirt className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-sm text-zinc-900">
                    {modalType === "dress"
                      ? "Dress Code Violation Details"
                      : "Behavior Disciplinary Referrals"}
                  </h3>
                  <p className="text-zinc-500 text-[11px]">
                    Student:{" "}
                    <span className="font-semibold text-zinc-700">
                      {selectedStudent.last_name}, {selectedStudent.first_name}
                    </span>{" "}
                    • Term: {activeTerm?.name || "Current Term"}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCloseModal}
                className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Content Body */}
            <div className="p-5 overflow-y-auto space-y-3 custom-scroll flex-1">
              {modalLoading ? (
                <div className="py-12 flex flex-col items-center justify-center text-zinc-400">
                  <Loader2 className="w-5 h-5 animate-spin mb-2 text-zinc-500" />
                  <span>Loading violation history...</span>
                </div>
              ) : studentViolations.length > 0 ? (
                studentViolations.map((item, index) => {
                  const dateVal = item.date_of_infraction || item.date_of_incident;
                  const dateFormatted = dateVal
                    ? new Date(dateVal).toLocaleString("en-US", {
                        dateStyle: "medium",
                        timeStyle: item.time_of_incident ? undefined : "short",
                      })
                    : "N/A";

                  return (
                    <div
                      key={item.id || index}
                      className="p-3.5 bg-zinc-50/80 border border-zinc-200/90 rounded-lg space-y-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-zinc-800 text-xs flex items-center gap-1.5">
                          <AlertTriangle
                            className={`w-3.5 h-3.5 shrink-0 ${
                              modalType === "dress" ? "text-amber-600" : "text-rose-600"
                            }`}
                          />
                          {modalType === "dress"
                            ? item.specific_item || "Uniform Infraction"
                            : item.violation_type || "Behavior Referral"}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500 font-medium bg-white px-2 py-0.5 border border-zinc-200 rounded-md">
                          <Clock className="w-3 h-3 text-zinc-400" />
                          {dateFormatted}
                        </span>
                      </div>

                      {/* Details & Badges */}
                      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-200/60 text-[11px]">
                        {modalType === "dress" ? (
                          <>
                            <span className="text-zinc-500">Intervention:</span>
                            <span className="capitalize font-semibold text-zinc-700 bg-amber-50/80 border border-amber-200 px-2 py-0.5 rounded text-[10px]">
                              {item.intervention_applied || "Verbal Warning"}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-zinc-500">Level:</span>
                            <span
                              className={`font-semibold px-2 py-0.5 rounded text-[10px] ${
                                item.infraction_level === "MAJOR"
                                  ? "bg-rose-100 text-rose-800 border border-rose-300"
                                  : "bg-amber-50 text-amber-800 border border-amber-200"
                              }`}
                            >
                              {item.infraction_level || "MINOR"}
                            </span>
                          </>
                        )}

                        {/* Logged/Referred By Name Formatting */}
                        {(item.logged_by_name || item.referring_teacher_name) && (
                          <span className="ml-auto text-[10px] text-zinc-400 flex items-center gap-1">
                            <User className="w-3 h-3 text-zinc-400" />
                            By{" "}
                            {(() => {
                              const rawName =
                                item.logged_by_name ||
                                item.referring_teacher_name ||
                                "";
                              
                              // If name already contains a comma ("Last, First"), return as-is
                              if (rawName.includes(",")) return rawName;

                              // Otherwise reformat "First Last" -> "Last, First"
                              const parts = rawName.trim().split(" ");
                              if (parts.length >= 2) {
                                const lastName = parts.pop();
                                const firstName = parts.join(" ");
                                return `${lastName}, ${firstName}`;
                              }
                              return rawName;
                            })()}
                          </span>
                        )}
                      </div>

                      {/* Detailed Notes / Incident Narrative */}
                      {(item.detailed_note || item.incident_description) && (
                        <div className="pt-1.5 text-[11px] text-zinc-600 bg-white p-2 border border-zinc-200/70 rounded flex items-start gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" />
                          <p className="leading-relaxed">
                            {item.detailed_note || item.incident_description}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="py-8 text-center text-zinc-400 text-xs italic">
                  No records found for this student in this term.
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50/80 flex items-center justify-between">
              <span className="text-[11px] text-zinc-500">
                Total Logged:{" "}
                <strong className="text-zinc-700">
                  {studentViolations.length}
                </strong>
              </span>
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-3 py-1.5 bg-white border border-zinc-200 hover:bg-zinc-100 text-zinc-700 rounded-lg font-semibold text-xs transition-colors shadow-2xs cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CitizenshipTab;